import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import { router } from '@forge/bridge';

import { CONTACT_US_URL, DOCS_URL } from '../../../../common/constants';
import { AppContext } from '../interfaces';
import { fetchAll, updateFormOpened } from '../store/incidentBannersSlice';
import { AppDispatch, RootState } from '../store/store';

import Banner from './Banner';
import DeleteBannerModal from './DeleteBannerModal';
import BannerForm from './form';
import { AddBanner, Wrapper } from './indexStyled';

interface Props {
    context: AppContext;
}

const IncidentBannersIssueContext: React.FC<Props> = ({ context }) => {
    const { canEdit, canView, issues, isLoading, isFormOpened, banners, users } = useSelector(
        (state: RootState) => state.incidentBanners
    );

    const dispatch = useDispatch<AppDispatch>();

    const fetchParams = useMemo(
        () => ({
            accountId: context.accountId || '',
            projectId: context.extension.project.id,
        }),
        [context.accountId, context.extension.project.id]
    );

    useEffect(() => {
        const fetchData = async () => {
            await dispatch(fetchAll({ projectId: context.extension.project.id }));
        };

        void fetchData();
    }, [dispatch, fetchParams]);

    if (isLoading) return <Spinner size='medium' />;
    if (!canView) return null;

    return (
        <>
            {isFormOpened ?
                <BannerForm context={context} />
            :   <Wrapper>
                    <SectionMessage
                        title='Incident banners'
                        actions={[
                            <SectionMessageAction onClick={() => router.open(DOCS_URL)}>Documentation</SectionMessageAction>,
                            <SectionMessageAction onClick={() => router.open(CONTACT_US_URL)}>
                                Contact support
                            </SectionMessageAction>,
                        ]}
                    >
                        <p>
                            Create banners associated with this issue that will remain visible on the customer portal until you
                            resolve the request.
                        </p>
                    </SectionMessage>
                    {(banners).map(banner => (
                        <Banner
                            data={banner}
                            issues={issues}
                            users={users}
                            siteUrl={context.siteUrl}
                            key={banner.key}
                        />
                    ))}
                    {canEdit && <AddBanner onClick={() => dispatch(updateFormOpened(true))}>+ Add banner</AddBanner>}
                </Wrapper>
            }
            <DeleteBannerModal />
        </>
    );
};

export default IncidentBannersIssueContext;
